/**
 * Admin / Handler self-proofreading page — 2-column layout:
 *   LEFT  → in-app reader (PDF / DOCX / EPUB)
 *   RIGHT → existing CurrentWork form (fault entries)
 *
 * The reader tracks current page/chapter and any selected text. Clicking the
 * "Voeg Fout by Huidige Posisie" button dispatches a CustomEvent that
 * CurrentWork (modified below) listens for and uses to pre-populate a new fault row.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Info, PlusCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import CurrentWork from '../proofreader/CurrentWork';
import BookReader from '../../components/BookReader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProofreading = () => {
  const { bookId } = useParams();
  const location = useLocation();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewOnly, setViewOnly] = useState(false);
  const [bookFileUrl, setBookFileUrl] = useState(null);
  const [bookMediaType, setBookMediaType] = useState(null);
  const [position, setPosition] = useState({ page: '', chapter: '', totalPages: null });
  const [selectedText, setSelectedText] = useState('');

  useEffect(() => {
    const fetchBook = async () => {
      if (!bookId) {
        setLoading(false);
        return;
      }
      let workLoaded = false;
      try {
        // Try the active-proofreading record first (full fault-capture mode).
        const r = await axios.get(`${API}/proofreading/work/${bookId}`);
        if (r.data && r.data.id) {
          setBook(r.data);
          workLoaded = true;
        }
      } catch (e) {
        // Not in active proofreading (e.g. viewing from another tab) — fall back
        // to read-only view mode below.
      }
      try {
        const info = await axios.get(`${API}/proofreading/reader-info/${bookId}`);
        if (info.data && info.data.url && info.data.media_type) {
          setBookFileUrl(`${BACKEND_URL}${info.data.url}`);
          setBookMediaType(info.data.media_type);
          if (!workLoaded) {
            // Read-only "Sien Boek" mode — no active proofreading record.
            setViewOnly(true);
            setBook({ id: bookId, boek_naam: location.state?.boekNaam || 'Boek' });
          }
        } else if (!workLoaded) {
          toast.error('Geen leesbare lêer beskikbaar nie');
        }
      } catch (infoErr) {
        console.warn('reader-info failed', infoErr);
        if (!workLoaded) {
          toast.error(infoErr?.response?.data?.detail || 'Kon nie boek laai nie');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchBook();
  }, [bookId, location.state]);

  const handlePrefillFault = () => {
    // Dispatch a CustomEvent — CurrentWork listens for this and adds a new pre-filled fault row.
    const detail = {
      bladsy: position.page || '',
      hoofstuk: position.chapter || '',
      oorspronklike_teks: selectedText || '',
    };
    window.dispatchEvent(new CustomEvent('proofreader-prefill-fault', { detail }));
    toast.success('Nuwe fout-ry bygevoeg met huidige posisie' + (selectedText ? ' en geselekteerde teks' : ''));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!book) {
    return (
      <div className="space-y-4">
        <Link to="/admin/books">
          <Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4 mr-2" /> Terug na Boeke Bestuur</Button>
        </Link>
        <div className="p-6 bg-amber-50 border border-amber-200 rounded text-amber-900">
          Geen boek is huidig deur jou besig om te proeflees nie.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-proofreading-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/admin/books">
          <Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4 mr-2" /> Terug na Boeke Bestuur</Button>
        </Link>
        <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          {viewOnly ? 'Sien Boek' : 'Proeflees'}: {book.boek_naam}
        </h2>
      </div>

      {!viewOnly && (
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 flex gap-2">
        <Info className="h-5 w-5 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">Personeel modus — proeflees binne die stelsel:</p>
          <ul className="list-disc list-inside space-y-1 mt-1">
            <li>Die boek vertoon links — rol of skuif tussen bladsye/hoofstukke.</li>
            <li>Selekteer foutige teks in die leeser, klik <strong>&ldquo;Voeg Fout by Huidige Posisie&rdquo;</strong> — &apos;n nuwe fout-ry word outomaties bygevoeg met die bladsy, hoofstuk en geselekteerde teks ingevul.</li>
            <li>By indien gaan die foute regstreeks na die skrywer vir regstellings (geen admin-goedkeuring nodig nie).</li>
            <li>Geen foute gevind? Klik <strong>&ldquo;Geen Foute Gevind&rdquo;</strong> regs — die boek gaan direk na <em>Wag vir Goedkeuring</em> vir publikasie.</li>
          </ul>
        </div>
      </div>
      )}

      {viewOnly ? (
        <BookReader
          bookFileUrl={bookFileUrl}
          mediaType={bookMediaType}
          bookmarkKey={bookId}
        />
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 space-y-3">
          <div className="flex items-center justify-between bg-white border rounded px-3 py-2 sticky top-0 z-10">
            <div className="text-sm">
              <span className="font-semibold">Huidige posisie:</span>{' '}
              {position.page && <span>Bladsy <strong>{position.page}</strong>{position.totalPages ? ` / ${position.totalPages}` : ''}</span>}
              {position.chapter && <span> &middot; Hoofstuk <strong>{position.chapter}</strong></span>}
              {!position.page && !position.chapter && <span className="text-gray-500">Begin lees om posisie te volg</span>}
            </div>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handlePrefillFault}
              data-testid="add-fault-from-position-btn"
              title="Voeg 'n nuwe fout-ry by met die huidige bladsy/hoofstuk/geselekteerde teks ingevul"
            >
              <PlusCircle className="h-4 w-4 mr-1" />
              Voeg Fout by Huidige Posisie
            </Button>
          </div>

          <BookReader
            bookFileUrl={bookFileUrl}
            mediaType={bookMediaType}
            onPositionChange={setPosition}
            onTextSelected={setSelectedText}
            bookmarkKey={bookId}
          />

          {selectedText && (
            <div className="text-xs bg-yellow-50 border border-yellow-200 rounded p-2">
              <strong>Geselekteerde teks:</strong>{' '}
              <span className="italic">&ldquo;{selectedText.length > 200 ? selectedText.slice(0, 200) + '…' : selectedText}&rdquo;</span>
            </div>
          )}

          {/* Saved-faults table portal slot — CurrentWork renders the table here
              when the proofreader has captured one or more faults. Keeping it
              on the LEFT (under the reader) makes the table wide and keeps the
              right-hand editor panel compact. */}
          <div id="proofreader-fault-list-slot" />
        </div>

        <div className="lg:col-span-2">
          <CurrentWork />
        </div>
      </div>
      )}
    </div>
  );
};

export default AdminProofreading;
